import { z } from "zod";

const OPENAI_MODELS = {
  O3_MINI: "o3-mini",
  GPT_4O: "gpt-4o",
  GPT_4O_MINI: "gpt-4o-mini",
  GPT_3_5_TURBO: "gpt-3.5-turbo",
  O1_PREVIEW: "o1-preview",
  O1_MINI: "o1-mini",
  GPT_4_5_PREVIEW: "gpt-4.5-preview",
} as const;

export const GEMINI_MODELS = {
  GEMINI_2_5_PRO: "gemini-2.5-pro-exp-03-25",
  GEMINI_2_0_FLASH: "gemini-2.0-flash",
  GEMINI_2_0_FLASH_LITE: "gemini-2.0-flash-lite",
  GEMINI_1_5_FLASH: "gemini-1.5-flash",
  GEMINI_1_5_FLASH_8B: "gemini-1.5-flash-8b",
  GEMINI_1_5_PRO: "gemini-1.5-pro",
} as const;

// Provider-specific model list converted to zod
export const openAIModelLabels = [
  OPENAI_MODELS.O3_MINI,
  OPENAI_MODELS.GPT_4O_MINI,
  OPENAI_MODELS.GPT_4O,
  OPENAI_MODELS.GPT_4_5_PREVIEW,
  OPENAI_MODELS.GPT_3_5_TURBO,
  OPENAI_MODELS.O1_PREVIEW,
  OPENAI_MODELS.O1_MINI,
] as const;

export const geminiModelLabels = [
  GEMINI_MODELS.GEMINI_2_5_PRO,
  GEMINI_MODELS.GEMINI_2_0_FLASH,
  GEMINI_MODELS.GEMINI_1_5_PRO,
] as const;

export const ANTHROPIC_MODELS = {
  CLAUDE_3_7_SONNET: "claude-3-7-sonnet-latest",
  CLAUDE_3_5_HAIKU: "claude-3-5-haiku-latest",
} as const;

export const anthropicModelLabels = [
  ANTHROPIC_MODELS.CLAUDE_3_7_SONNET,
  ANTHROPIC_MODELS.CLAUDE_3_5_HAIKU,
] as const;

export type ModelName =
  | (typeof openAIModelLabels)[number]
  | (typeof anthropicModelLabels)[number]
  | (typeof geminiModelLabels)[number]
  | "mock"
  | "mock_slow";

// Validation utility functions using zod
export function isAnthropicModel(
  modelName: string,
): modelName is (typeof anthropicModelLabels)[number] {
  // @ts-expect-error
  return anthropicModelLabels.includes(modelName as ModelName);
}

export function isOpenAIModel(
  modelName: string,
): modelName is (typeof openAIModelLabels)[number] {
  // @ts-expect-error
  return openAIModelLabels.includes(modelName as ModelName);
}

export function isGeminiModel(model: string): model is ModelName {
  return geminiModelLabels.includes(
    model as (typeof geminiModelLabels)[number],
  );
}

export function isModelName(modelName: string): modelName is ModelName {
  return (
    isOpenAIModel(modelName) ||
    isAnthropicModel(modelName) ||
    isGeminiModel(modelName)
  );
}

// Provider type
export const OPENAI_PROVIDER = "openai";
export const ANTHROPIC_PROVIDER = "anthropic";
export const GEMINI_PROVIDER = "gemini";
export const FAKE_PROVIDER = "mock-ai";
export type Provider =
  | typeof OPENAI_PROVIDER
  | typeof ANTHROPIC_PROVIDER
  | typeof GEMINI_PROVIDER
  | typeof FAKE_PROVIDER;
export const ProviderSchema = z.enum([
  OPENAI_PROVIDER,
  ANTHROPIC_PROVIDER,
  GEMINI_PROVIDER,
  FAKE_PROVIDER,
]);

// Model display info for UI
export const ModelInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  provider: ProviderSchema,
  description: z.string(),
});
export type ModelInfo = z.infer<typeof ModelInfoSchema>;

// Mapping model to provider - create with proper indexed type
export const MODEL_PROVIDER_MAP: Record<string, Provider> = {
  ...Object.fromEntries(openAIModelLabels.map((key) => [key, OPENAI_PROVIDER])),
  ...Object.fromEntries(
    anthropicModelLabels.map((key) => [key, ANTHROPIC_PROVIDER]),
  ),
  ...Object.fromEntries(geminiModelLabels.map((key) => [key, GEMINI_PROVIDER])),
};

// Model display information for UI
export const MODELS_INFO: ModelInfo[] = [
  {
    id: OPENAI_MODELS.O3_MINI,
    name: "O3 Mini",
    provider: "openai",
    description: "Smaller, faster version of O3",
  },
  {
    id: ANTHROPIC_MODELS.CLAUDE_3_7_SONNET,
    name: "Claude 3.7 Sonnet",
    provider: "anthropic",
    description: "Latest Claude model with excellent reasoning",
  },
  {
    id: OPENAI_MODELS.O1_MINI,
    name: "O1 Mini",
    provider: "openai",
    description: "Smaller, faster version of O1 Preview",
  },
  {
    id: OPENAI_MODELS.O1_PREVIEW,
    name: "O1 Preview",
    provider: "openai",
    description: "OpenAI's preview model",
  },
  {
    id: OPENAI_MODELS.GPT_4O_MINI,
    name: "GPT-4o Mini",
    provider: "openai",
    description: "Smaller, faster version of GPT-4o",
  },
  {
    id: ANTHROPIC_MODELS.CLAUDE_3_5_HAIKU,
    name: "Claude 3.7 Haiku",
    provider: "anthropic",
    description: "Fast, cost-effective Claude model",
  },
  {
    id: OPENAI_MODELS.GPT_4_5_PREVIEW,
    name: "GPT-4.5 Preview",
    provider: "openai",
    description: "OpenAI's preview model",
  },
  {
    id: OPENAI_MODELS.GPT_3_5_TURBO,
    name: "GPT-3.5 Turbo",
    provider: "openai",
    description: "Fast and cost-effective for simpler tasks",
  },
  {
    id: OPENAI_MODELS.GPT_4O,
    name: "GPT-4o",
    provider: "openai",
    description: "OpenAI's most capable model for a wide range of tasks",
  },
  {
    id: GEMINI_MODELS.GEMINI_2_5_PRO,
    name: "Gemini 2.5 Pro",
    provider: GEMINI_PROVIDER,
    description: "Enhanced thinking and reasoning, multimodal understanding",
  },
  {
    id: GEMINI_MODELS.GEMINI_2_0_FLASH,
    name: "Gemini 2.0 Flash",
    provider: GEMINI_PROVIDER,
    description:
      "Next generation features, speed, thinking, realtime streaming",
  },
  {
    id: GEMINI_MODELS.GEMINI_1_5_PRO,
    name: "Gemini 1.5 Pro",
    provider: GEMINI_PROVIDER,
    description: "Complex reasoning tasks requiring more intelligence",
  },
];

// assert that every model has a provider and description
// TODO write a test for this
